import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isEmpty } from 'lodash';
import uuid from 'uuid/v1';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import * as atlasOps from '../../../api/Atlas/atlas-operations';
import methodPromise from '../../../modules/method-promise-wrapper';
import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';

import LeafletMap from '../../components/LeafletMap';

const { defaultMapCenter } = Meteor.settings.public;
let addDrawEventListeners;
let addOnlineBaseMap;
let createGeometryDrawControls;
let drawCustomButtons;
let drawAIInterestArea;
let showGeometryEditControls;

class MapContainerAIAtlas extends Component {
  aiAtlasMap = null;

  aiAtlasMapComponent = React.createRef();

  aiInterestAreaDrawControl = null;

  aiInterestAreaEditControl = null;

  aiInterestAreaLayer = null;

  randomMapId = uuid();

  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'aiAtlasMapComponent.current.state.isMapLoaded', false),
    isAIAtlasMapLoaded: false,
    maximumarea: 100
  };

  componentDidMount() {
    this.fetchSettings();
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  componentDidUpdate(prevProps) {
    const { aiInterestArea, atlas } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isAIAtlasMapLoaded } = this.state;
    const { aiInterestArea: prevAIInterestArea, atlas: prevAtlas } = prevProps;

    if (clientModulesReady && isLeafletMapLoaded && !isAIAtlasMapLoaded) {
      this.initializeMap();
    }

    if (isAIAtlasMapLoaded) {
      if (!isEqual(atlas, prevAtlas)) {
        drawCustomButtons({
          map: {
            selectedButtons: {
              Attribution: true,
              Home: true,
              QRCode: false,
              Scale: true,
              Search: false,
              Title: true,
              Zoom: true
            },
            mapLocation: defaultMapCenter,
            name: atlasOps.getName(atlas),
            publicId: atlasOps.getId(atlas),
            shared: atlasOps.isShared(atlas)
          },
          type: 'atlas',
          leafletMap: this.aiAtlasMap
        });
      }

      if (!isEqual(aiInterestArea, prevAIInterestArea)) {
        drawAIInterestArea({
          aiInterestArea,
          aiInterestAreaLayer: this.aiInterestAreaLayer,
          isUpdate: true,
          leafletMap: this.aiAtlasMap
        });

        if (!isEmpty(aiInterestArea)) {
          this.aiAtlasMap.removeControl(this.aiInterestAreaDrawControl);
        }

        showGeometryEditControls({
          leafletMap: this.aiAtlasMap,
          drawControl: this.aiInterestAreaDrawControl,
          editControl: this.aiInterestAreaEditControl,
          defineGeometryActive: true,
          geometryLayer: this.aiInterestAreaLayer
        });
      }
    }
  }

  fetchSettings = async () => {
    try {
      const AISettings = await methodPromise('admin.fetchAISetting');
      if (AISettings.length > 0) {
        this.setState({ maximumarea: AISettings[0].LimitSueface });
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  getCurrentMapState = () => {
    const { setAIInterestArea } = this.props;

    return {
      itemType: 'atlas',
      leafletMap: this.aiAtlasMap,
      defineGeometryActive: true,
      geometryLayer: this.aiInterestAreaLayer,
      drawControl: this.aiInterestAreaDrawControl,
      editControl: this.aiInterestAreaEditControl,
      setAtlasGeometry: setAIInterestArea
    };
  };

  updateMapState = newState => this.setState({ isLeafletMapLoaded: newState });

  initializeMap = () => {
    const { aiInterestArea, atlas } = this.props;

    this.aiAtlasMap = get(this, 'aiAtlasMapComponent.current.leafletMap');
    this.aiAtlasMap.setView(
      featurePoint2LatLng(defaultMapCenter),
      featureGetZoom(defaultMapCenter)
    );

    drawCustomButtons({
      map: {
        selectedButtons: {
          Attribution: true,
          Home: true,
          QRCode: false,
          Scale: true,
          Search: false,
          Title: true,
          Zoom: true
        },
        mapLocation: defaultMapCenter,
        name: atlasOps.getName(atlas),
        publicId: atlasOps.getId(atlas),
        shared: atlasOps.isShared(atlas)
      },
      leafletMap: this.aiAtlasMap
    });

    addOnlineBaseMap(this.aiAtlasMap);

    this.aiInterestAreaLayer = drawAIInterestArea({
      aiInterestArea,
      leafletMap: this.aiAtlasMap
    });

    const aiInterestAreaControls = createGeometryDrawControls({
      item: atlas,
      drawnItems: this.aiInterestAreaLayer
    });
    this.aiInterestAreaDrawControl = aiInterestAreaControls.drawControlFull;
    this.aiInterestAreaEditControl = aiInterestAreaControls.drawControlEdit;

    addDrawEventListeners({
      limitation: true,
      maximumarea: this.state.maximumarea,
      leafletMap: this.aiAtlasMap,
      getCurrentMapState: this.getCurrentMapState,
      editControl: this.aiInterestAreaEditControl
    });

    showGeometryEditControls({
      leafletMap: this.aiAtlasMap,
      drawControl: this.aiInterestAreaDrawControl,
      editControl: this.aiInterestAreaEditControl,
      defineGeometryActive: true,
      geometryLayer: this.aiInterestAreaLayer
    });

    this.setState({ isAIAtlasMapLoaded: true });
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        addDrawEventListeners,
        addOnlineBaseMap,
        createGeometryDrawControls,
        drawCustomButtons,
        drawAIInterestArea,
        showGeometryEditControls
      } = await import('../../../modules/leaflet-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;
    return (
      <LeafletMap
        deployEnv={deployEnv}
        isAtlas
        isRounded
        mapId={this.randomMapId}
        ref={this.aiAtlasMapComponent}
        updateMapState={this.updateMapState}
      />
    );
  }
}

MapContainerAIAtlas.propTypes = {
  aiInterestArea: PropTypes.object.isRequired,
  atlas: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  setAIInterestArea: PropTypes.func.isRequired
};

export default MapContainerAIAtlas;
